import { Case, CaseOrderSummary } from "@types-runway/case";

import { countOf, isNewer } from "./accountOrders";

const noValue = "—";

const createdTime = (createdAt?: string) => (createdAt ? new Date(createdAt).getTime() : 0);

const isNewerCase = (candidate: Case, current: Case) =>
  createdTime(candidate.createdAt) - createdTime(current.createdAt) > 0 ||
  (createdTime(candidate.createdAt) === createdTime(current.createdAt) &&
    (candidate.id ?? 0) > (current.id ?? 0));

const newestFirst =
  <Item>(isNewerThan: (candidate: Item, current: Item) => boolean) =>
  (first: Item, second: Item) => {
    if (isNewerThan(first, second)) return -1;
    if (isNewerThan(second, first)) return 1;

    return 0;
  };

// ACCT-R9: the Cases, the most recently created first; Cases created together rank by number.
export const listCasesNewestFirst = (cases: Case[]) => [...cases].sort(newestFirst(isNewerCase));

// ACCT-R12: a Case's Orders, the most recently created first, as the latest Order is chosen.
export const listOrdersNewestFirst = (orders: CaseOrderSummary[]) =>
  [...orders].sort(newestFirst(isNewer));

// ACCT-R9: "Cases and Orders", the number of Cases and the number of Orders on them.
export const titleCasesAndOrders = (cases: Case[]) => {
  const orderCount = cases.reduce((count, { orders }) => count + (orders?.length ?? 0), 0);

  return `Cases and Orders · ${countOf(cases.length, "Case")} · ${countOf(orderCount, "Order")}`;
};

interface ShipsToAddress {
  street: string;
  city: string;
  state: string;
}

// ACCT-R12: the street, the city and the state of the Case's shipping address.
// A blank part is left out, and an address with no part reads as a dash.
export const describeOrderShipsTo = (address?: ShipsToAddress | null) => {
  const parts = [address?.street, address?.city, address?.state]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(", ") : noValue;
};
